import React, { useState, useEffect, useCallback } from "react";
import AddQuestionForm from "./AddQuestionForm";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "./config";
import QuestionUpload from "./QuestionUpload";
import Pagination from "./Pagination";
import Popup from "./Popup";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchQuestionTypes,
  fetchQuestionsThunk,
  fetchQuestionVariantsThunk,
  editVariantThunk,
} from "../components/questionBank/redux/questionBankThunk";

import {
  setSelectedQuestionTypeId,
  clearSubQuestionVariants,
  setVariantEditingQuestion,
  clearVariantEditingQuestion,
} from "../components/questionBank/redux/questionBankSlice";

import {
  fetchClassesThunk,
  fetchSubjectsByClassThunk,
  fetchSectionsBySubjectThunk,
  fetchChaptersThunk,
} from "./chapter/redux/chapterThunk";
import {
  setSelectedClassId,
  setSelectedSectionId,
  setSelectedSubjectId,
  setSelectedChapterId,
} from "./chapter/redux/chapterSlice";
import {
  setEditingQuestion,
  addQuestion,
  updateQuestion,
  addQuestionsFromFile,
} from "../components/questionBank/redux/questionBankSlice";

const QuestionBank = () => {
  const dispatch = useDispatch();
  const {
    questionTypes,
    selectedQuestionTypeId,
    loadingQuestionTypes,
    errorQuestionTypes,
  } = useSelector((state) => state.questionBank);

  const {
    classes,
    subjects,
    sections,
    chapters,
    selectedClassId,
    selectedSubjectId,
    selectedSectionId,
    selectedChapterId,
    loadingClasses,
    loadingSections,
    loadingSubjects,
    loadingChapters,
  } = useSelector((state) => state.chapter);

  //const [selectedChapterId, setSelectedChapterId] = useState("");

  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [isProcessingAction, setIsProcessingAction] = useState(false); // For Add/Upload/Delete operations
  const [isEditing, setIsEditing] = useState(false);
  const [addVariantModal, setaddVariantModal] = useState(false);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [showVariantAiModal, setShowVariantAiModal] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [uploadedData, setUploadedData] = useState(null);

  // for generate ai api
  const [popupData, setPopupData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiVariant, setAiVariant] = useState({
    isLoading: false,
    error: "",
    rephrasedText: "",
  });

  //For variant
  const [editVariantModal, setEditVariantModal] = useState(false);
  const [questionText, setQuestionText] = useState("");
  const [difficultyLevel, setDifficultyLevel] = useState("");
  const [questionType, setQuestionType] = useState("");
  const [options, setOptions] = useState([]);
  const editVariantData = useSelector(
    (state) => state.questionBank.variantEditingQuestion
  );
  // Error states (centralized for better management)
  const [error, setError] = useState(null);

  //For matching
  const [selectedMatchData, setSelectedMatchData] = useState(null);
  const [showMatchModal, setShowMatchModal] = useState(false);

  const navigate = useNavigate();
  const userStr = localStorage.getItem("user");
  const userId = userStr ? JSON.parse(userStr).id : null;

  const questionTypeMapping = {
    "fill in the blanks": 15,
    "one word answer": 8,
    "answer in brief": 10,
    "definition of terms": 9,
    "difference between": 11,
    "multiple choice": 1,
    "true/false": 3,
    "short answer": 4,
    "long answer": 5,
    "draw diagram": 12,
    "label diagram": 13,
    "writing section": 14,
    "match the following": 16,
    "activity based questions": 17,
  };

  //---For Pagination----------
  const [currentPage, setCurrentPage] = useState(1);
  //const totalPages = 100; // From your API response
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);

  //Checkboxes
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const handleCheckboxChange = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((item) => item !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Fetch questions or data for that page here
    // dispatch(fetchQuestionsThunk({ page, ...otherFilters }));
  };

  const onLimitChange = (e) => {
    const val = Number(e.target.value);
    if (val > 0) {
      setLimit(val);
      setPage(1); // Reset to first page when limit changes
    }
  };

  // --- Data Fetching Effects (Ordered by Dependency) ---

  useEffect(() => {
    dispatch(fetchClassesThunk());
  }, [dispatch]);

  useEffect(() => {
    if (selectedClassId) {
      dispatch(fetchSubjectsByClassThunk(selectedClassId));
    }
  }, [dispatch, selectedClassId]);

  useEffect(() => {
    if (selectedClassId && selectedSubjectId) {
      dispatch(fetchSectionsBySubjectThunk({ subjectId: selectedSubjectId }));

      dispatch(
        fetchChaptersThunk({
          classId: selectedClassId,
          subjectId: selectedSubjectId,
        })
      );
      dispatch(
        fetchQuestionTypes({
          classId: selectedClassId,
          subjectId: selectedSubjectId,
        })
      );
    }
  }, [dispatch, selectedClassId, selectedSubjectId, selectedSectionId]);

  useEffect(() => {
    dispatch(fetchQuestionTypes());
  }, [dispatch]);

  //if exist in local it will store
  useEffect(() => {
    const savedId = localStorage.getItem("selectedChapterId");
    if (savedId) {
      dispatch(setSelectedChapterId(savedId));
    }
  }, []);

  // 5. Fetch Questions when all main filters are selected (including question type)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (selectedClassId && selectedSubjectId && selectedChapterId) {
        dispatch(
          fetchQuestionsThunk({
            classId: selectedClassId,
            subjectId: selectedSubjectId,
            sectionId: selectedSectionId,
            chapterId: selectedChapterId,
            questionTypeId: selectedQuestionTypeId,
            page: currentPage,
            limit: limit,
          })
        );
      }
      setError(null);
    }, 900); // ms debounce

    return () => clearTimeout(timeout); // Cleanup on dependency change
  }, [
    selectedClassId,
    selectedSubjectId,
    selectedSectionId,
    selectedChapterId,
    selectedQuestionTypeId,
    currentPage,
    limit,
    dispatch,
  ]); // Add selectedQuestionTypeId to dependency array
  const questions = useSelector((state) => state.questionBank.questions);
  const totalPages = useSelector((state) => state.questionBank.totalPages);

  // const paginationData =
  //   questions.length > 0 ? questions[questions.length - 1] : null;
  // const totalPages = paginationData?.pagination.totalPages||10; // From your API response
  //alert(totalPages)
  const loading = useSelector((state) => state.questionBank.loading);

  //To fetch variants of particular questions
  const subQuestionData = useSelector(
    (state) => state.questionBank.subQuestionVariants
  );

  const variantLoading = useSelector(
    (state) => state.questionBank.variantLoading
  );
  const variantError = useSelector((state) => state.questionBank.variantError);
  const handleViewClick = (question) => {
    dispatch(
      fetchQuestionVariantsThunk({
        classId: selectedClassId,
        subjectId: selectedSubjectId,
        chapterId: selectedChapterId,
        parentQuestionId: question.question_id,
      })
    );

    setShowVariantModal(true);
  };

  const handleEditVariantModal = (subQ) => {
    dispatch(setVariantEditingQuestion(subQ));
    setQuestionText(subQ.question_text);
    setDifficultyLevel(subQ.difficulty_name);
    setOptions(subQ.options);
    setQuestionType(subQ.type_name); // just for display (not editable)
    setShowVariantModal(false);
    setEditVariantModal(true);
  };

  const handleEditVariantSubmit = async (e) => {
    e.preventDefault();

    const parentQuestion = subQuestionData; // Full parent question from Redux
    const variant = editVariantData; // Sub-question selected for edit
    if (!parentQuestion || !variant) return;

    // Update the sub-question inside the parent
    const updatedSubQuestions = parentQuestion.subQuestions.map((subQ) =>
      subQ.question_id === variant.question_id
        ? {
            ...subQ,
            question_text: questionText.trim(),
            options: options,
          }
        : subQ
    );
    console.log("sub before", updatedSubQuestions);

    const payload = {
      ...parentQuestion, // contains existing created_by, updated_by
      subQuestions: updatedSubQuestions,
    };

    try {
      const resultAction = await dispatch(
        editVariantThunk({
          questionPayload: payload,
          userId: localStorage.getItem("user_id"),
        })
      );

      if (editVariantThunk.fulfilled.match(resultAction)) {
        dispatch(clearVariantEditingQuestion());
        setEditVariantModal(false);
        alert("✅ Variant updated successfully.");
      } else {
        alert("❌ Failed to update variant: " + resultAction.payload);
      }
    } catch (error) {
      console.error("Variant update error:", error);
      alert("❌ Unexpected error occurred.");
    }
  };

  const handleDeleteVariant = async (variantId) => {
    if (!variantId) return;

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/questions/${variantId}`
      );

      console.log("Variant deleted successfully:", response.data);

      // Optional: Refresh the variants list
      dispatch(
        fetchQuestionVariantsThunk({
          classId: selectedClassId,
          subjectId: selectedSubjectId,
          chapterId: selectedChapterId,
          parentQuestionId: subQuestionData.question_id,
        })
      );

      alert("Variant deleted successfully.");
    } catch (error) {
      console.error("Delete failed:", error);
      alert(
        `Failed to delete variant: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  };

  const closeVariantModal = () => {
    dispatch(clearSubQuestionVariants());
    setShowVariantModal(false);
  };

  //To upload data to server for variant
  const handleUpload = async () => {
    const userStr = localStorage.getItem("user");
    const userId = userStr ? JSON.parse(userStr).id : null;

    if (!userId || !uploadedData || !uploadedData.questions?.length) {
      setError("Missing user ID or uploaded questions.");
      return;
    }

    if (!selectedClassId || !selectedSubjectId || !selectedChapterId) {
      setError("Please select a Class, Subject, and Chapter before uploading.");
      return;
    }
    console.log("Uploaded Data ", uploadedData);
    const questionsToUpload = uploadedData.questions.map((q) => ({
      ...q,
      question_typeid:
        questionTypeMapping[q.question_type?.toLowerCase()?.trim()] ||
        q.question_typeid ||
        null,
      link_id: parseInt(selectedQuestionId, 10),
      class_id: parseInt(selectedClassId, 10),
      subject_id: parseInt(selectedSubjectId, 10),
      chapter_id: parseInt(selectedChapterId, 10),
      created_by: userId,
      options: q.options || [], // ensure empty array if not present
      images: q.images || [],
      matching_options: q.matching_options || [],
      subQuestions: q.subQuestions || [],
    }));

    console.log("question to upload", questionsToUpload);

    const payload = {
      created_by: userId,
      class_id: parseInt(selectedClassId, 10),
      subject_id: parseInt(selectedSubjectId, 10),
      chapter_id: parseInt(selectedChapterId, 10),
      questions: questionsToUpload,
    };

    try {
      const response = await axios.post(
        `${API_BASE_URL}/questions/bulk-upload`,
        payload
      );
      console.log("Upload success:", response.data);

      setUploadedData(null);
      closeAddVariantModal(); // Use closeModal to reset states properly
      setError("Questions uploaded successfully."); // Display success message
    } catch (error) {
      console.error("Upload failed:", error);
      setError(
        `Failed to upload questions: ${
          error.message || "An error occurred during upload."
        }`
      );
    }
  };

  const handleAddQuestionsFromFile = async (fileQuestions) => {
    if (!selectedClassId || !selectedSubjectId || !selectedChapterId) {
      alert(
        "Please select a Class, Subject, and Chapter before uploading questions."
      );
      setError(
        "Please select a Class, Subject, and Chapter to upload questions."
      );
      return;
    }

    const classIdInt = parseInt(selectedClassId, 10);
    const subjectIdInt = parseInt(selectedSubjectId, 10);
    const chapterIdInt = parseInt(selectedChapterId, 10);

    if (isNaN(classIdInt) || isNaN(subjectIdInt) || isNaN(chapterIdInt)) {
      alert(
        "Invalid Class, Subject, or Chapter ID selected for file upload. Please re-select from the dropdowns."
      );
      setError(
        "One or more selected IDs are invalid for file upload. Please check your selections."
      );
      return;
    }

    setIsProcessingAction(true);
    setError(null);
    try {
      const questionsWithCategory = fileQuestions.map((q) => ({
        ...q,
        class_id: classIdInt,
        subject_id: subjectIdInt,
        chapter_id: chapterIdInt,
      }));

      console.log(
        "Sending bulk upload payload from QuestionBank:",
        questionsWithCategory
      );

      const response = await fetch(`${API_BASE_URL}/questions/bulk-upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(questionsWithCategory),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! Status: ${response.status}`
        );
      }

      const result = await response.json();
      alert(`Questions from file added successfully! ${result.message || ""}`);
      const queryParams = new URLSearchParams();
      queryParams.append("class_id", selectedClassId);
      queryParams.append("subject_id", selectedSubjectId);
      queryParams.append("chapter_id", selectedChapterId);
      if (selectedQuestionTypeId) {
        // Include question type in refetch
        queryParams.append("questionType", selectedQuestionTypeId);
      }
      dispatch(
        fetchQuestionsThunk({
          classId: selectedClassId,
          subjectId: selectedSubjectId,
          chapterId: selectedChapterId,
          questionTypeId: selectedQuestionTypeId,
        })
      );
    } catch (err) {
      console.error("Error adding questions from file:", err);
      setError(`Failed to add questions from file: ${err.message}`);
      alert(`Failed to add questions from file: ${err.message}`);
    } finally {
      setIsProcessingAction(false);
    }
  };
  const handleEditQuestion = async (question) => {
    dispatch(setEditingQuestion(question));
    console.log("data:", question);
  };

  const handleGenerateAnswer = async (q) => {
    setShowAiModal(null); // clear old data
    setShowAiModal(true); // open the modal immediately
    try {
      setIsGenerating(true);
      const bodyData = {
        id: q.question_id,
        text: q.question_text,
        className: q.class_name,
        subject: q.subject_name,
        chapter: q.chapter_name,
        options: {
          A: q.options[0]?.text || "",
          B: q.options[1]?.text || "",
          C: q.options[2]?.text || "",
          D: q.options[3]?.text || "",
        },
      };

      const resp = await fetch(
        "https://web1024.docmaster.in:5007/llamaRoutes/mcq/answer",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(bodyData),
        }
      );

      const data = await resp.json();
      setPopupData(data ?? "No answer returned.");
    } catch (e) {
      console.error(e);
      setPopupData("⚠️ Failed to generate answer.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateVariant = async (q) => {
    let questionText = q.question_text;
    setAiVariant({
      isLoading: true,
      error: "",
      rephrasedText: "",
    });
    setShowVariantAiModal(true); // Open the modal immediately
    if (q.question_typeid === 15) {
      let blankIndex = 0;
      questionText = questionText.replace(
        /_+/g,
        () => `<<BLANK_${blankIndex++}>>`
      );
    }

    try {
      const response = await fetch(
        "https://web1024.docmaster.in:5007/llamaRoutes/rephrase",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text: questionText,
            type_id: q.question_typeid,
          }),
          mode: "cors",
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Unknown error");
      }
      // debugger;
      setAiVariant({
        isLoading: false,
        error: "",
        rephrasedText: `Original Question:\n${q.question_text}\n\n${
          data.result.replace(/\\n/g, "\n") || "No answer returned."
        }`,
      });
    } catch (err) {
      setAiVariant({
        isLoading: false,
        error: err.message,
        rephrasedText: "",
      });
    }
  };

  const handleDeleteQuestion = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this question? This action cannot be undone."
      )
    ) {
      return;
    }
    setIsProcessingAction(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/questions/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! Status: ${response.status}`
        );
      }

      alert("Question deleted successfully!");
      const queryParams = new URLSearchParams();
      queryParams.append("class_id", selectedClassId);
      queryParams.append("subject_id", selectedSubjectId);
      queryParams.append("chapter_id", selectedChapterId);
      if (selectedQuestionTypeId) {
        // Include question type in refetch
        queryParams.append("questionType", selectedQuestionTypeId);
      }
      dispatch(
        fetchQuestionsThunk({
          classId: selectedClassId,
          subjectId: selectedSubjectId,
          chapterId: selectedChapterId,
          questionTypeId: selectedQuestionTypeId,
        })
      );
    } catch (err) {
      console.error("Error deleting question:", err);
      setError(`Failed to delete question: ${err.message}`);
      alert(`Failed to delete question: ${err.message}`);
    } finally {
      setIsProcessingAction(false);
    }
  };

  // UI reordering only; does not persist changes to backend
  // const moveQuestion = (index, direction) => {
  //   setQuestions((prev) => {
  //     const newQuestions = [...prev];
  //     const targetIndex = direction === "up" ? index - 1 : index + 1;
  //     if (targetIndex < 0 || targetIndex >= newQuestions.length) return prev;
  //     [newQuestions[index], newQuestions[targetIndex]] = [
  //       newQuestions[targetIndex],
  //       newQuestions[index],
  //     ];
  //     return newQuestions;
  //   });
  // };

  const goToAnswerPage = () => {
    navigate("/answer-questions");
  };

  const goToTestPage = () => {
    navigate("/answer-questions");
  };

  // Check if all necessary category filters are selected (excluding question type, which is optional)
  const selectedCategoryValid =
    selectedClassId && selectedSubjectId && selectedChapterId;

  // Determine if any loading or processing is happening
  const isLoading =
    loadingClasses ||
    loadingSubjects ||
    loadingChapters ||
    loadingQuestionTypes ||
    loadingQuestions ||
    isProcessingAction; // Include new loading state

  const handleParsedData = (data) => {
    console.log("data");
  };
  const handleAddClick = (id) => {
    setSelectedQuestionId(id);
    setaddVariantModal(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API_BASE_URL}/upload`, formData);
      let data = response.data;

      // Assign unique client-side IDs if not already present
      if (data && Array.isArray(data.questions)) {
        data.questions = data.questions.map((q) => ({
          ...q,
          id: q.id || uuidv4(), // Use existing ID or generate a new one
        }));
      }

      setUploadedData(data);
      localStorage.setItem("uploadedQuestions", JSON.stringify(data));
      setIsEditing(false); // Ensure edit mode is off after new upload
      dispatch(setEditingQuestion(null));
    } catch (error) {
      console.error("File upload failed:", error);
      // Optionally, show an error message to the user
    }
  };

  const closeAddVariantModal = () => {
    setaddVariantModal(false);
    setSelectedQuestionId(null);
  };

  // console.log("Uploading for question ID:", selectedQuestionId);
  // console.log("Selected file:", file.name);

  const filteredQuestions = questions.filter((q) => {
    if (selectedQuestionTypeId === "") return true;
    const selectedType = questionTypes.find(
      (type) => type.type_id.toString() === selectedQuestionTypeId
    );
    return q.type_name === selectedType?.type_name;
  });

  return (
    <div className="container-fluid w-100 mx-auto py-2 font-inter">
      <div className="row mx-auto w-100">
        <div className="col-md-12 w-100">
          <h3 className="text-3xl font-bold text-center mb-6 text-gray-800">
            Question Bank
          </h3>
          {/* Error Display Section */}
          {error && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-2 max-w-3xl mx-auto"
              role="alert"
            >
              <span className="block sm:inline"> {error}</span>
            </div>
          )}
          {/* Filters + Form block */}
          <div className="bg-white  mb-8 max-w-4xl mx-auto">
            <div className="grid grid-cols-1 py-4 ps-3 shadow rounded-3  sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-4">
              {/* Filter 1: Class */}
              <div className="row w-100">
                <div className="col-md-6 ">
                  <div className="row mb-2">
                    <label
                      htmlFor="class-select"
                      className="block text-sm col-sm-4 col-form-label font-medium text-gray-700 mb-1"
                    >
                      Select Class:
                    </label>
                    <div className="col-sm-8">
                      <select
                        value={selectedClassId}
                        onChange={(e) => {
                          dispatch(setSelectedClassId(e.target.value));
                          dispatch(setSelectedSubjectId(""));
                        }}
                        className="w-full p-2 form-control border border-gray-300 rounded-md"
                      >
                        <option value="">-- Select Class --</option>
                        {classes.map((cls) => (
                          <option key={cls.class_id} value={cls.class_id}>
                            {cls.class_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Filter 2: Subject */}
                <div className="col-md-6 ">
                  <div className="row mb-2">
                    <label
                      htmlFor="subject-select"
                      className="block text-sm col-sm-4 col-form-label font-medium text-gray-700 mb-1"
                    >
                      Select Subject:
                    </label>
                    <div className="col-sm-8">
                      <select
                        value={selectedSubjectId}
                        onChange={(e) =>
                          dispatch(setSelectedSubjectId(e.target.value))
                        }
                        className="w-full p-2 form-control border border-gray-300 rounded-md"
                        disabled={!selectedClassId || loadingSubjects}
                      >
                        <option value="">-- Select Subject --</option>
                        {subjects.map((sub) => (
                          <option key={sub.subject_id} value={sub.subject_id}>
                            {sub.subject_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {sections.length > 0 && (
                  <div className="col-md-6 ">
                    <div className="row mb-2">
                      <label
                        htmlFor="section-select"
                        className="block text-sm col-sm-4 col-form-label font-medium text-gray-700 mb-1"
                      >
                        Select Section:
                      </label>
                      <div className="col-sm-8">
                        <select
                          value={selectedSectionId}
                          onChange={(e) =>
                            dispatch(setSelectedSectionId(e.target.value))
                          }
                          className="w-full p-2 form-control border border-gray-300 rounded-md"
                          disabled={loadingSections}
                        >
                          <option value="">-- Select Section --</option>
                          {sections.map((sec) => (
                            <option key={sec.section_id} value={sec.section_id}>
                              {sec.section_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Filter 3: Chapter */}
                <div className="col-md-6 ">
                  <div className="row mb-2">
                    <label
                      htmlFor="chapter-select"
                      className="block text-sm col-sm-4 col-form-label font-medium text-gray-700 mb-1"
                    >
                      Select Chapter:
                    </label>
                    <div class="col-sm-8">
                      <select
                        id="chapter-select"
                        value={selectedChapterId}
                        onChange={(e) => {
                          const value = e.target.value; // ✅ define value
                          dispatch(setSelectedChapterId(value));
                          localStorage.setItem("selectedChapterId", value);
                        }}
                        className="w-full p-2 form-control border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={
                          !selectedSubjectId ||
                          !selectedClassId ||
                          loadingChapters ||
                          chapters.length === 0
                        }
                      >
                        <option value="">-- Select Chapter --</option>
                        {loadingChapters ? (
                          <option disabled>Loading chapters...</option>
                        ) : chapters.length === 0 &&
                          selectedSubjectId &&
                          selectedClassId ? (
                          <option disabled>No chapters for this subject</option>
                        ) : (
                          chapters.map((chap) => (
                            <option
                              key={chap.chapter_id}
                              value={chap.chapter_id.toString()}
                            >
                              {chap.chapter_name}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Filter 4: Question Type */}
                <div className="col-md-6 ">
                  <div className="row mb-2">
                    <label
                      htmlFor="question-type-select"
                      className="block text-sm col-sm-4 col-form-label font-medium text-gray-700 mb-1"
                    >
                      Select Question Type:
                    </label>
                    <div class="col-sm-8">
                      <select
                        id="question-type-select"
                        value={selectedQuestionTypeId}
                        onChange={(e) => {
                          dispatch(setSelectedQuestionTypeId(e.target.value));
                          localStorage.setItem(
                            "selectedQuestionTypeId",
                            e.target.value
                          );
                        }}
                        className="w-full form-control p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={
                          loadingQuestionTypes ||
                          questionTypes.length === 0 ||
                          isLoading
                        }
                      >
                        <option value="">-- All Types --</option>
                        {loadingQuestionTypes ? (
                          <option disabled>Loading types...</option>
                        ) : questionTypes.length === 0 ? (
                          <option disabled>No types available</option>
                        ) : (
                          questionTypes.map((type) => (
                            <option
                              key={type.type_id}
                              value={type.type_id.toString()}
                            >
                              {type.type_name}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              {/* Validation Message */}
              {!selectedCategoryValid && (
                <p className="text-center text-red-600 mb-2">
                  Please select a <strong>Class</strong>,{" "}
                  <strong>Subject</strong>, and <strong>Chapter</strong> to add
                  or view questions.
                </p>
              )}

              {classes.length === 0 && !loadingClasses && (
                <p className="text-center text-gray-600 mb-2">
                  No classes available. Please add classes first.
                </p>
              )}

              {/* Add Question Form neatly centered below */}
              <div className="row w-100">
                <div className="col-md-12">
                  <QuestionUpload onDataParsed={handleParsedData} />
                  <AddQuestionForm />
                </div>
              </div>
            </div>
          </div>

          {/* <QuestionPopup onSubmit={handleAddQuestion} questionTypes={questionTypes} /> */}

          {/* Action Buttons */}
          {/* <div className="mt-6 flex justify-center space-x-4">
                <button
                    onClick={goToAnswerPage}
                    disabled={questions.length === 0 || isLoading}
                    className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition duration-300 ease-in-out shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Answer Questions
                </button>
                <button
                    onClick={goToTestPage}
                    disabled={questions.length === 0 || isLoading}
                    className="bg-purple-600 text-white px-6 py-3 rounded-md hover:bg-purple-700 transition duration-300 ease-in-out shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Go to Test Page
                </button>
            </div> */}

          <hr className="my-8 border-gray-300" />

          {/* Questions List */}
          {loading ? (
            <p className="text-center text-gray-600">Loading questions...</p>
          ) : questions.length === 0 &&
            selectedCategoryValid &&
            !loadingQuestions ? (
            <p className="text-center text-gray-600">
              No questions available for the selected category/type. Add a
              question to get started.
            </p>
          ) : (
            <div className="grid grid-cols-1 rounded-3 p-1 bg-white shadow gap-4 relative ">
              <div className="table-responsive p-2 rounded-3">
                <MathJaxContext
                  config={{
                    loader: { load: ["input/tex", "output/chtml"] },
                    tex: {
                      inlineMath: [
                        ["\\(", "\\)"],
                        ["$", "$"],
                      ],
                    },
                  }}
                >
                  <table className="table  table-striped table-bordered text-15">
                    <tbody>
                      <tr className="bg-gray-200 text-gray-700">
                        <th className="text-center">#</th>
                        <th className="text-center">ID</th>
                        <th className="text-center">Question Id</th>
                        <th className="text-center">Type</th>
                        <th className="text-center">Class</th>
                        <th className="text-center">Subject</th>
                        <th className="text-center">Chapter</th>
                        <th className="text-center">Upload</th>
                        <th className="text-center">Difficulty</th>
                        <th
                          className="text-center"
                          style={{ minWidth: "110px" }}
                        >
                          Actions
                        </th>
                      </tr>
                      {filteredQuestions
                        .filter((q) => q && q.question_id)
                        .map((q) => (
                          <tr key={q.question_id} className="hover:bg-gray-100">
                            <td className="text-center">
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(q.question_id)}
                                onChange={() =>
                                  handleCheckboxChange(q.question_id)
                                }
                              />
                            </td>

                            <td className="text-center">
                              <span key={q.question_id}> {q.question_id}</span>
                            </td>

                            <td className="text-wrap">
                              <MathJax dynamic>
                                {q.question_typeid === 6
                                  ? q.passage_text
                                  : q.question_text}
                              </MathJax>
                            </td>

                            <td className="text-center">{q.type_name}</td>
                            <td className="text-center">{q.class_name}</td>
                            <td className="text-center">{q.subject_name}</td>
                            <td className="text-center">{q.chapter_name}</td>
                            <td className="text-center">
                              {q.question_typeid === 16 ? ( //16 is for matching
                                <button
                                  onClick={() => {
                                    setSelectedMatchData(q); // q is the full question object
                                    setShowMatchModal(true);
                                  }}
                                >
                                  View More
                                </button>
                              ) : (
                                <>
                                  <button
                                    onClick={() =>
                                      handleAddClick(q.question_id)
                                    }
                                  >
                                    Add
                                  </button>
                                  <button onClick={() => handleViewClick(q)}>
                                    View
                                  </button>
                                </>
                              )}
                            </td>

                            <td className="text-center">{q.difficulty_name}</td>
                            <td className="text-center">
                              <div className="flex justify-center space-x-2">
                                <button
                                  onClick={() =>
                                    handleDeleteQuestion(q.question_id)
                                  }
                                  disabled={isLoading}
                                  className="bg-red-500 btn btn-sm btn-outline-danger   hover:bg-red-600 transition duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <i class="bi bi-trash"></i>
                                </button>
                                <button
                                  onClick={() => handleEditQuestion(q)}
                                  disabled={isLoading}
                                  className="   btn btn-sm btn-outline-primary    transition duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <i class="bi bi-pencil-square"></i>
                                </button>
                                {selectedQuestionTypeId === "1" && (
                                  <button
                                    onClick={() => handleGenerateAnswer(q)}
                                    disabled={isLoading}
                                    aria-label="Generate Answer"
                                    className="bg-red-500 btn btn-sm btn-outline-danger   hover:bg-red-600 transition duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed "
                                  >
                                    <i
                                      className="bi bi-lightbulb"
                                      title="Generate Answer"
                                    ></i>
                                  </button>
                                )}
                                <button
                                  onClick={() => handleGenerateVariant(q)}
                                  disabled={isLoading}
                                  aria-label="Generate Answer"
                                  className="bg-red-500 btn btn-sm btn-outline-danger   hover:bg-red-600 transition duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed "
                                >
                                  <i
                                    className="bi bi-magic"
                                    title="Generate Variants"
                                  ></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </MathJaxContext>
              </div>

              {/* Popup */}
              <div>
                {addVariantModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0,0,0,0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1000,
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        padding: "20px",
                        width: "90%",
                        maxWidth: "1200px",
                        maxHeight: "90vh",
                        overflowY: "auto",
                        borderRadius: "8px",
                        position: "relative",
                      }}
                    >
                      <button
                        onClick={() => closeAddVariantModal()}
                        style={{
                          position: "absolute",
                          top: "10px",
                          right: "15px",
                          fontSize: "18px",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        ❌
                      </button>
                      <h2>Upload Question File</h2>
                      <input
                        type="file"
                        accept=".json,.xlsx"
                        onChange={handleFileUpload}
                      />

                      {uploadedData?.questions?.length > 0 && (
                        <div className="grid grid-cols-1 rounded-3 p-1 bg-white shadow gap-4 relative mt-4">
                          <div className="table-responsive p-2 rounded-3">
                            <table className="table table-striped table-bordered text-15 w-full">
                              <thead>
                                <tr className="bg-gray-200 text-gray-700">
                                  <th className="text-center">#</th>
                                  <th className="text-center">Question</th>
                                  <th className="text-center">Options</th>
                                  <th className="text-center">Type ID</th>
                                  <th className="text-center">Class ID</th>
                                  <th className="text-center">Subject ID</th>
                                  <th className="text-center">Chapter ID</th>
                                  <th className="text-center">Difficulty ID</th>
                                </tr>
                              </thead>
                              <tbody>
                                {uploadedData.questions.map((q, index) => (
                                  <tr
                                    key={q.id || index}
                                    className="hover:bg-gray-100"
                                  >
                                    <td className="text-center">{index + 1}</td>
                                    <td className="text-wrap">
                                      {q.question_text}
                                    </td>
                                    <td className="text-wrap max-w-xs min-w-[150px] break-words">
                                      <ol
                                        type="A"
                                        className="pl-4 list-decimal"
                                      >
                                        {Array.isArray(q.options) &&
                                          q.options.map((opt, i) => (
                                            <li key={i}>{opt.text}</li>
                                          ))}
                                      </ol>
                                    </td>
                                    <td className="text-center">
                                      {q.question_typeid}
                                    </td>
                                    <td className="text-center">
                                      {uploadedData.class_id}
                                    </td>
                                    <td className="text-center">
                                      {uploadedData.subject_id}
                                    </td>
                                    <td className="text-center">
                                      {uploadedData.chapter_id}
                                    </td>
                                    <td className="text-center">
                                      {q.difficulty_id}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <button onClick={handleUpload}>Upload</button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {editVariantModal && editVariantData && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0,0,0,0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1000,
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        padding: "20px",
                        width: "35%",
                        maxWidth: "1200px",
                        maxHeight: "90vh",
                        overflowY: "auto",
                        borderRadius: "8px",
                        position: "relative",
                      }}
                    >
                      <button
                        onClick={() => {
                          dispatch(clearVariantEditingQuestion());
                          setEditVariantModal(false);
                        }}
                        style={{
                          position: "absolute",
                          top: "10px",
                          right: "15px",
                          fontSize: "18px",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        ❌
                      </button>
                      <div className="p-4 bg-white rounded shadow-lg w-full max-w-xl mx-auto">
                        <h2 className="text-xl font-bold text-gray-800 mb-4">
                          Edit Variant Question
                        </h2>

                        <form
                          onSubmit={handleEditVariantSubmit}
                          className="space-y-6"
                        >
                          <div className="mb-3 row">
                            <label className="col-sm-3 col-form-label text-end">
                              Question Type
                            </label>
                            <div className="col-sm-9">
                              <select className="form-control" disabled>
                                <option>{questionType}</option>
                              </select>
                            </div>
                          </div>

                          <div className="mb-3 row">
                            <label className="col-sm-3 col-form-label text-end">
                              Difficulty Level
                            </label>
                            <div className="col-sm-9">
                              <select className="form-control" disabled>
                                <option>{difficultyLevel}</option>
                              </select>
                            </div>
                          </div>

                          <div className="mb-3 row">
                            <label className="col-sm-3 col-form-label text-end">
                              Question Text
                            </label>
                            <div className="col-sm-9">
                              <textarea
                                className="form-control"
                                rows="2"
                                value={questionText}
                                onChange={(e) =>
                                  setQuestionText(e.target.value)
                                }
                                placeholder="Enter updated question text"
                              />
                            </div>
                          </div>
                          {options.length > 0 && (
                            <div className="mb-3 row">
                              <label className="col-sm-3 col-form-label text-end">
                                Options
                              </label>
                              <div className="col-sm-9">
                                <ul className="options-list">
                                  {options.map((option, index) => (
                                    <div
                                      className="col-sm-12 mb-2"
                                      key={option.detail_id || index}
                                    >
                                      <input
                                        type="text"
                                        value={option.text}
                                        placeholder={`Option ${index + 1}`}
                                        className="w-full p-2 border form-control border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                        onChange={(e) => {
                                          const updatedOptions = options.map(
                                            (opt, i) =>
                                              i === index
                                                ? {
                                                    ...opt,
                                                    text: e.target.value,
                                                  }
                                                : opt
                                          );
                                          setOptions(updatedOptions);
                                        }}
                                      />
                                    </div>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          )}

                          <div className="d-flex justify-content-end gap-2 mt-4">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setEditVariantModal(false)}
                            >
                              Cancel
                            </button>
                            <button type="submit" className="btn btn-primary">
                              Update
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                )}

                {showVariantModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0,0,0,0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1000,
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        padding: "20px",
                        width: "90%",
                        maxWidth: "1200px",
                        maxHeight: "90vh",
                        overflowY: "auto",
                        borderRadius: "8px",
                        position: "relative",
                      }}
                    >
                      <button
                        onClick={closeVariantModal}
                        style={{
                          position: "absolute",
                          top: "10px",
                          right: "15px",
                          fontSize: "18px",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        ❌
                      </button>

                      {subQuestionData?.subQuestions &&
                      Array.isArray(subQuestionData.subQuestions) ? (
                        <div className="bg-gray-100 p-4 rounded mb-4">
                          <h2 className="text-lg font-semibold mb-2">
                            Variants
                          </h2>
                          <div className="mt-4">
                            <table className="table table-bordered table-striped table-hover text-sm">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-4 py-2 text-left">ID</th>
                                  <th className="px-4 py-2 text-left">
                                    Question
                                  </th>
                                  <th className="px-4 py-2 text-left">Type</th>
                                  <th className="px-4 py-2 text-left">Class</th>
                                  <th className="px-4 py-2 text-left">
                                    Subject
                                  </th>
                                  <th className="px-4 py-2 text-left">
                                    Chapter
                                  </th>
                                  <th className="px-4 py-2 text-left">
                                    Difficulty
                                  </th>
                                  <th className="px-4 py-2 text-left">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {subQuestionData.subQuestions.map((subQ) => (
                                  <tr key={subQ.question_id}>
                                    <td className="px-4 py-2">
                                      {subQ.question_id}
                                    </td>
                                    <td className="px-4 py-2">
                                      {subQ.question_text}
                                    </td>
                                    <td className="px-4 py-2">
                                      {subQ.type_name}
                                    </td>
                                    <td className="px-4 py-2">
                                      {subQ.class_name}
                                    </td>
                                    <td className="px-4 py-2">
                                      {subQ.subject_name}
                                    </td>
                                    <td className="px-4 py-2">
                                      {subQ.chapter_name}
                                    </td>
                                    <td className="px-4 py-2">
                                      {subQ.difficulty_name}
                                    </td>
                                    <td className="px-4 py-2">
                                      <button
                                        onClick={() =>
                                          handleEditVariantModal(subQ)
                                        }
                                        className="text-blue-600 hover:underline text-sm"
                                      >
                                        Edit
                                      </button>{" "}
                                      |{" "}
                                      <button
                                        onClick={() =>
                                          handleDeleteVariant(subQ.question_id)
                                        }
                                        className="text-red-600 hover:underline text-sm"
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <p className="text-red-500 font-semibold">
                          ❗ Sub-questions not found. {variantError}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {showAiModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0,0,0,0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1000,
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        padding: "20px",
                        width: "90%",
                        maxWidth: "600px",
                        maxHeight: "90vh",
                        overflowY: "auto",
                        borderRadius: "8px",
                        position: "relative",
                      }}
                    >
                      <button
                        onClick={() => {
                          setShowAiModal(false);
                        }}
                        style={{
                          position: "absolute",
                          top: "10px",
                          right: "15px",
                          fontSize: "18px",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        ❌
                      </button>

                      <div>
                        {isGenerating ? (
                          <p>⏳ Generating answer...</p>
                        ) : (
                          <div>
                            <div>Correct Option :{popupData.answer}</div>
                            <div>Reason :&nbsp;{popupData.reason}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {showVariantAiModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0,0,0,0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1000,
                    }}
                    onClick={() => setShowVariantAiModal(false)}
                  >
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        background: "#fff",
                        padding: "20px",
                        width: "90%",
                        maxWidth: "900px",
                        maxHeight: "90vh",
                        overflowY: "auto",
                        borderRadius: "8px",
                        position: "relative",
                      }}
                    >
                      <button
                        onClick={() => setShowVariantAiModal(false)}
                        style={{
                          position: "absolute",
                          top: "10px",
                          right: "15px",
                          fontSize: "18px",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        ❌
                      </button>

                      <div style={{ marginTop: "30px" }}>
                        {aiVariant.isLoading ? (
                          <p>⏳ Generating variants...</p>
                        ) : aiVariant.error ? (
                          <p style={{ color: "red" }}>⚠️ {aiVariant.error}</p>
                        ) : (
                          <div style={{ whiteSpace: "pre-line" }}>
                            {aiVariant.rephrasedText}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {showMatchModal && selectedMatchData && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0,0,0,0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1000,
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        padding: "20px",
                        width: "90%",
                        maxWidth: "1200px",
                        maxHeight: "90vh",
                        overflowY: "auto",
                        borderRadius: "8px",
                        position: "relative",
                      }}
                    >
                      <button
                        onClick={() => setShowMatchModal(false)}
                        style={{
                          position: "absolute",
                          top: "10px",
                          right: "15px",
                          fontSize: "18px",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        ❌
                      </button>

                      {selectedMatchData &&
                      Array.isArray(selectedMatchData.matching_options) ? (
                        <>
                          <h2 className="text-xl font-semibold mb-4 text-gray-800">
                            {selectedMatchData.question_text}
                          </h2>

                          <table className="table table-bordered table-striped table-hover text-sm">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="p-3 border-r text-center font-semibold">
                                  Column A
                                </th>
                                <th className="p-3 text-center font-semibold">
                                  Column B
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedMatchData.matching_options.map(
                                (pair) => (
                                  <tr
                                    key={pair.matching_id}
                                    className="border-t"
                                  >
                                    <td className="p-3 border-r text-gray-800">
                                      {pair.column1_text}
                                    </td>
                                    <td className="p-3 text-gray-800">
                                      {pair.column2_text}
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </>
                      ) : (
                        <p className="text-red-500 font-semibold">
                          ❗ Matching data not available.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* {questions.map((q, index) => (
                <div
                  key={q.question_id}
                  className=" p-3  questions-lytout m-1 rounded-2 border border-gray-200"
                >
                  <h6 className="text-lg font-semibold text-gray-800 mb-2">
                    Type: {q.type_name}
                  </h6>
                  {q.group_stat === "parent" && q.passage_text && (
                    <>
                      <p className="font-bold">Passage:</p>
                      <p className="text-gray-700 mb-3 ml-4 italic">
                        {q.passage_text}
                      </p>
                    </>
                  )}
                  <p className="text-gray-700 mb-3">
                    <b>
                      <i>Question</i>: {q.question_text}
                    </b>
                  </p>

                  {q.options &&
                    q.options.length > 0 && ( // Changed from question_details to options
                      <ul className="list-disc w-100 ml-6 text-sm text-gray-600 ">
                        {q.options.map((opt, optIndex) => (
                          <li className="w-100 text-wrap" key={opt.detail_id || optIndex}>
                            {opt.text}{" "}
                            {opt.is_correct ? "(Correct Answer)" : ""}{" "}
                          
                          </li>
                        ))}
                      </ul>
                    )}

                  {q.subQuestions &&
                    q.subQuestions.length > 0 && ( // Changed from sub_questions to subQuestions
                      <>
                        <p className="font-bold">Sub-questions:</p>
                        {q.subQuestions.map((subQ, subIndex) => (
                          <div
                            key={subQ.question_id || subIndex}
                            className="ml-4 mb-2 p-2 border-l-2 border-gray-300"
                          >
                            <p className="text-gray-700">
                              Q.{subIndex + 1} ({subQ.type_name}):{" "}
                              {subQ.question_text}
                            </p>
                            {subQ.options &&
                              subQ.options.length > 0 && ( // Changed from question_details to options
                                <ul className="list-disc w-100 ml-6 text-sm text-gray-600">
                                  {subQ.options.map((opt, optIndex) => (
                                    <li className="w-100" key={opt.detail_id || optIndex}>
                                      {opt.text}{" "}
                                      {opt.is_correct ? "(Correct Answer)" : ""}
                                    </li>
                                  ))}
                                </ul>
                              )}
                          </div>
                        ))}
                      </>
                    )}

                  <div className="text-sm text-gray w-100 ">
                    {q.class_name && `Class: ${q.class_name}`}
                    {q.subject_name && ` | Subject: ${q.subject_name}`}
                    {q.chapter_name && ` | Chapter: ${q.chapter_name}`}
                    {q.marks && ` | Marks: ${q.marks}`}
                    {q.difficulty_name && ` | Difficulty: ${q.difficulty_name}`}
                  </div>
                  <div className="flex items-center space-x-2">
                    
                    <div className="absolute right-0 top-0 mt-2 mr-2 flex btns-section space-x-2">
                      <button
                        onClick={() => handleDeleteQuestion(q.question_id)}
                        disabled={isLoading}
                        className="bg-red-500 btn btn-outline-danger   hover:bg-red-600 transition duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => handleEditQuestion(q)}
                        disabled={isLoading}
                        className="   btn btn-outline-primary    transition duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                  
                </div>
              ))} */}
                <div className="d-flex justify-content-center align-items-center">
                  {
                    <label>
                      Items per page (limit):{" "}
                      <input
                        type="number"
                        min={1}
                        value={limit}
                        onChange={onLimitChange}
                        className="form-input w-30 border p-1 rounded"
                        style={{ width: "15%" }}
                      />
                    </label>
                  }
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionBank;
